import { CommsService } from '@acaprojects/ngx-composer';
import { Injectable } from '@angular/core';

import { BehaviorSubject } from 'rxjs';

export interface ICaterer {
    id: string;
    name: string;
    name_start?: string;
    name_end?: string;
    description: string;
    hours: { open: number; close: number; };
    cutoff: { fixed: number; relative: number; };
    img_url: string;
    min_cost: number;
    opening_hours: { day: string; time: string }[];
    phone: string;
    options: ICateringCategory[];
}

export interface ICateringCategory {
    id: number | string;
    name: string;
    items: ICateringItem[];
    minimum: number;
    extra?: string;
}

export interface ICateringItem {
    id: string;
    name: string;
    description: string;
    img_url: string;
    minimum: number;
    price: number;
    cutoff: { fixed: number; relative: number; };
    properties: ICateringItemProperty[];
    quantity?: number;
}

export interface ICateringItemProperty {
    id: string;
    name: string;
    type: number;
    values: ICateringItemPropertyValue[];
}

export interface ICateringItemPropertyValue {
    id: number | string;
    name: string;
    default?: boolean;
    min?: number;
    max?: number;
    step?: number;
    extra_price: number;
}

export interface ICartItem {
    id: string;
    item: ICateringItem;
    quantity: number;
    unit_price: number;
    details?: string;
    extras: any[];
}

export interface ICart {
    id: string;
    total: number;
    total_price: number;
    notes?: string;
    items: ICartItem[];
}

@Injectable({
    providedIn: 'root'
})
export class CateringService {
    public parent: any = null;

    private subjects: any = {};
    private observers: any = {};

    constructor(private http: CommsService) {
        this.subjects.caterers = new BehaviorSubject<ICaterer[]>([]);
        this.observers.caterers = this.subjects.caterers.asObservable();
        this.subjects.general_catering = new BehaviorSubject<ICaterer>(null);
        this.observers.general_catering = this.subjects.general_catering.asObservable();
        this.subjects.locations = new BehaviorSubject<string[]>([]);
        this.observers.locations = this.subjects.locations.asObservable();
    }

    /**
     * Initialise service
     */
    public init() {
        if (!this.parent && !this.parent.Settings.setup) {
            return setTimeout(() => this.init(), 500);
        }
        if (this.parent.Settings.get('app.catering.enabled')) {
            const sub = this.parent.Users.listen('state', (state) => {
                if (state === 'available') {
                    this.loadCaterers();
                    this.loadLocations();
                    sub.unsubscribe();
                }
            });
        }
    }

    public list() {
        const catering_list = (this.get('general_catering') || { options: [] }) as ICaterer;
        const categories: any = {};
        for (const cat of catering_list.options) {
            categories[cat.name] = cat.items;
        }
        return categories;
    }

    /**
     * Listen for changes to service property
     * @param name Name of the property. Possible values caterers, and locations
     * @param next Callback for changes to property
     */
    public listen(name: string, next: (value) => void) {
        return this.subjects[name] ? this.observers[name].subscribe(next) : null;
    }

    /**
     * Get value of service property
     * @param name Name of property
     */
    public get(name: string) {
        return this.subjects[name] ? this.subjects[name].getValue() : null;
    }

    /**
     * Get observable for property
     * @param name Name of the property. Possible values caterers, and locations
     */
    public observer(name: string) {
        return this.subjects[name] ? this.observers[name] : null;
    }

    /**
     * Get list of caterers
     */
    public caterers(): ICaterer[] {
        return this.subjects.caterers.getValue();
    }

    /**
     * Get list of catering delivery locations
     */
    public locations(): string[] {
        return this.subjects.locations.getValue();
    }

    /**
     * Post new catering order
     * @param id ID of the order
     * @param items Array of catering items
     * @param notes Additional notes to send caterer
     * @param location Delivery location
     * @param date Date and time of delivery
     */
    public order(id: string, items: any[], notes: string, location: string, date: any) {
        return new Promise((resolve, reject) => {
            if (items && items.length > 0) {
                let confirming = false;
                let cost = 0;
                for (const i of items) { cost += i.quantity * i.price; }
                this.parent.confirm({
                    title: `Confirm order?`,
                    message: `Your order will cost $${(cost / 100).toFixed(2)}`,
                    icon: 'shopping_cart',
                    accept: 'Ok',
                    cancel: true,
                }, (event) => {
                    if (event.type === 'Accept') {
                        confirming = true;
                        event.data.loading = true;
                        const request = {
                            id, items, notes, location, date
                        };
                        this.http.post(`${this.parent.api_endpoint}/orders`, request)
                            .subscribe((resp: any) => {
                                event.data.loading = false;
                                event.data.success = true;
                                this.parent.Analytics.event('Catering', 'created_catering_order');
                                setTimeout(() => {
                                    event.close();
                                    resolve(resp);
                                }, 2000);
                            }, (err: any) => {
                                event.data.loading = false;
                                event.data.error = true;
                                event.data.errorData = 'Request Error!';
                                this.parent.Analytics.event('Catering', 'create_catering_order_fail');
                                setTimeout(() => event.close(), 2000);
                                reject(err);
                            },
                        );
                    } else if (!confirming) {
                        reject();
                        event.close();
                    }
                });
            } else {
                reject('Cart is empty');
            }
        });
    }

    /**
     * Load caterer data from the API
     */
    private loadCaterers(tries: number = 0) {
        if (!this.parent) {
            return setTimeout(() => this.loadCaterers(), 500);
        }
        const url = `${this.parent.api_endpoint}/caterers`;
        this.http.get(url).subscribe(
            (caterers: any[]) => this.subjects.caterers.next(this.processCaterers(caterers)),
            (err) => {
                this.parent.log('CATERING(S)', 'Error loading caterings:', err, 'error');
                setTimeout(() => this.loadCaterers(tries), 500 * Math.min(20, ++tries));
            }, () => this.parent.log('CATERING(S)', 'Loaded caterings data')
        );
    }

    /**
     * Load catering delivery locations from API
     */
    private loadLocations(tries: number = 0) {
        if (!this.parent) {
            return setTimeout(() => this.loadLocations(), 500);
        }
        const url = `${this.parent.api_endpoint}/caterers/locations`;
        this.http.get(url).subscribe(
            (locations: any[]) => this.subjects.locations.next(locations),
            (err) => {
                this.parent.log('CATERING(S)', 'Error loading location data:', err, 'error');
                setTimeout(() => this.loadLocations(tries), 500 * Math.min(20, ++tries));
            }, () => this.parent.log('CATERINGS(S)', 'Loaded locations data')
        );
    }

    /**
     * Convert caterer data into local format
     * @param list Array of caterers
     */
    private processCaterers(list: any[]) {
        const caterer_list: ICaterer[] = [];
        for (const item of list) {
            const hours: any[] = [];
            for (const i of item.opening_hours) {
                const time = i.split(':').slice(1).join(':');
                hours.push({ day: i.split(':')[0], time });
            }
            const name = item.title.split(' ');
            caterer_list.push({
                id: item.id,
                name: item.title,
                name_end: ((name.length > 1 ? name.splice(name.length - 1, 1) : '') || ''),
                name_start: name.join(' '),
                description: item.description,
                hours: {
                    open: item.hour_open,
                    close: item.hour_close
                },
                cutoff: {
                    fixed: item.default_cutoff_fixed_seconds || item.default_cutoff_fixed,
                    relative: item.default_cutoff_relative_seconds || item.default_relative_fixed,
                },
                img_url: item.img_url,
                min_cost: item.minimum_cost,
                opening_hours: hours,
                phone: item.phone,
                options: this.processCategories(item.options)
            });
        }
        for (const caterer of caterer_list) {
            if (caterer.id.indexOf('general') >= 0) {
                this.subjects.general_catering.next(caterer_list.splice(caterer_list.indexOf(caterer), 1)[0]);
                break;
            }
        }
        return caterer_list;
    }

    /**
     * Convert caterer item catergory data into local format
     * @param categories Array of catering categories
     */
    private processCategories(categories: any[]) {
        const category_list: ICateringCategory[] = [];
        for (const item of categories) {
            category_list.push({
                id: item.id,
                name: item.name,
                extra: item.extra_information,
                minimum: item.minimum_order || item.minimum || 0,
                items: this.processCateringItems(item.items)
            });
        }
        return category_list;
    }

    /**
     * Convert caterer item data into local format
     * @param list Array of catering items
     */
    private processCateringItems(list: any[]) {
        const item_list: ICateringItem[] = [];
        for (const item of list) {
            item_list.push({
                id: item.id,
                name: item.name,
                description: item.description,
                img_url: item.img_url,
                minimum: item.minimum_order || item.minimum || 0,
                price: item.price,
                cutoff: {
                    fixed: item.cutoff_fixed_seconds || item.cutoff_fixed,
                    relative: item.cutoff_relative_seconds || item.cutoff_relative,
                },
                properties: this.processCateringProperties(item.properties)
            });
        }
        return item_list;
    }

    /**
     * Convert caterer item property data into local format
     * @param list Array of properties
     */
    private processCateringProperties(list: any) {
        const properties: ICateringItemProperty[] = [];
        for (const item of list) {
            const out = {
                id: item.id,
                name: item.name,
                type: item.type,
                values: []
            };
            for (const value of item.values) {
                out.values.push({
                    id: value.id,
                    name: value.name,
                    default: value.selected,
                    extra_price: value.extra_price,
                    min: value.min,
                    max: value.max,
                    step: value.step
                });
            }
            properties.push(out);
        }
        return properties;
    }
}
